import { fullstorySelectors } from '@fullstory/redux';
import {
  generateRoutePath,
  LibraryObjectType,
  LIBRARY_ROUTE,
  TYPE_FILTER_PARAM_KEY,
  TYPE_FILTER_PARAM_VALUES,
} from '@fullstory/routes';
import { createPath } from 'history';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LIBRARY_TABLE_INSIGHTS_COLUMN_KEY } from '../../components';
import { SORT_QUERY_PARAM } from '../../constants';

const OLD_INSIGHTS_SORT_KEY = 'insightSort';

/**
 * When Spaces is enabled, returns a path that can be used for redirection from old Library index/landing pages
 * to the Library view.
 *
 * The path is inclusive of search params that enable a deep link carrying over search, filter, and sort state
 * as well as the object type for pre-selection in the Library view.
 */
export const useSpacesRedirect = (objectType: LibraryObjectType): string => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const location = useLocation();
  const pathname = useMemo(() => {
    const path = generateRoutePath(LIBRARY_ROUTE, { orgId });
    const isHelpRoute = location.pathname.endsWith('/help');
    return `${path}${isHelpRoute ? '/help' : ''}`;
  }, [location.pathname, orgId]);
  const search = useMemo(() => {
    const params = new URLSearchParams(location.search);

    // params should match and carry over from the old LibraryObjectLanding params (except insights)
    // additionally set the type so the tag filter will be preselected
    params.set(TYPE_FILTER_PARAM_KEY, TYPE_FILTER_PARAM_VALUES[objectType]);

    // map over insights sort
    const sort = params.get(SORT_QUERY_PARAM);
    if (sort && sort.split(':')[0] === OLD_INSIGHTS_SORT_KEY) {
      const [sortColumnKey, sortDirection] = sort.split(':');
      if (sortColumnKey === OLD_INSIGHTS_SORT_KEY) {
        // reset it to the new sort column key
        // no need to worry about validation here
        const newSort = `${LIBRARY_TABLE_INSIGHTS_COLUMN_KEY}:${sortDirection}`;
        params.delete(SORT_QUERY_PARAM);
        params.set(SORT_QUERY_PARAM, newSort);
      }
    }

    return params.toString();
  }, [location.search, objectType]);

  return createPath({
    pathname,
    search,
  });
};
